import { fromUnixTime, differenceInMinutes } from 'date-fns';
import { leadImportStatuses } from '../leadImports.model';

/**
 * Time utilities for lead import jobs using date-fns
 * Following project convention for time manipulation [[memory:4654766]]
 */

const FIFTEEN_MINUTES = 15;

/**
 * Check if a job can be cancelled (QUEUED or PROCESSING for more than 15 minutes)
 * @param {Object} record - The job record
 * @returns {boolean}
 */
export const canCancelJob = (record) => {
  if (!record || !record.status) return false;

  // Only QUEUED and PROCESSING jobs can be cancelled
  const cancelableStatuses = [leadImportStatuses.QUEUED, leadImportStatuses.PROCESSING];
  if (!cancelableStatuses.includes(record.status)) return false;

  if (!record.createdAt) {
    return false;
  }

  const createdDate = fromUnixTime(record.createdAt);
  const now = new Date();
  const minutesElapsed = differenceInMinutes(now, createdDate);

  return minutesElapsed >= FIFTEEN_MINUTES;
};

/**
 * Check if a job can be rolled back 
 * Business rules:
 * - FAILED jobs that started processing (have startedAt)
 * - CANCELLED jobs that were processing when cancelled (have startedAt)
 * - ROLLBACK_FAILED jobs (previous rollback attempt failed, can retry)
 * - NEVER for COMPLETED jobs
 * - NEVER for CANCELLED jobs that were only QUEUED (no startedAt)
 * @param {Object} record - The job record
 * @returns {boolean}
 */
export const canRollbackJob = (record) => {
  if (!record) return false;

  // NO rollback for COMPLETED jobs regardless of time
  if (record.status === leadImportStatuses.COMPLETED) {
    return false;
  }

  // Rollback possible for FAILED, CANCELLED, or ROLLBACK_FAILED jobs that started processing
  const rollbackableStatuses = [leadImportStatuses.FAILED, leadImportStatuses.CANCELLED, leadImportStatuses.ROLLBACK_FAILED];
  if (rollbackableStatuses.includes(record.status) && record.startedAt) {
    return true;
  }

  return false;
};

/**
 * Get minutes remaining until an action can be performed
 * @param {Object} record - The job record
 * @param {string} actionType - 'cancel' or 'rollback'
 * @returns {number} Minutes remaining (0 if action is available)
 */
export const getMinutesUntilActionAvailable = (record, actionType) => {
  if (!record) return 0;

  let referenceTimestamp;

  if (actionType === 'cancel') {
    // Only calculate for QUEUED and PROCESSING jobs
    if (![leadImportStatuses.QUEUED, leadImportStatuses.PROCESSING].includes(record.status)) return 0;
    referenceTimestamp = record.createdAt;
  } else if (actionType === 'rollback') {
    // NO rollback timing for COMPLETED jobs - business rule change
    return 0;
  }

  if (!referenceTimestamp) return 0;

  const referenceDate = fromUnixTime(referenceTimestamp);
  const now = new Date();
  const minutesElapsed = differenceInMinutes(now, referenceDate);

  return Math.max(0, FIFTEEN_MINUTES - minutesElapsed);
};

/**
 * Get a human-readable time until action is available
 * @param {Object} record - The job record
 * @param {string} actionType - 'cancel' or 'rollback'
 * @returns {string} Human readable time remaining
 */
export const getTimeUntilActionAvailable = (record, actionType) => {
  const minutesRemaining = getMinutesUntilActionAvailable(record, actionType);

  if (minutesRemaining === 0) {
    return null;
  }

  if (minutesRemaining === 1) {
    return '1 minute remaining';
  }

  return `${minutesRemaining} minutes remaining`;
};
